import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import QRCode from 'qrcode.react';
import { Clock, UserCheck, Download, Upload, Users, QrCode } from 'lucide-react';
import AppLayout from '../layout/AppLayout';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { AttendanceRecord, AttendanceEvent } from '../../types/attendance';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

export default function AttendancePage() {
  useDocumentTitle('Attendance');
  
  const [events, setEvents] = useState<AttendanceEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<AttendanceEvent | null>(null);
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showQR, setShowQR] = useState(false);
  const [manualAttendance, setManualAttendance] = useState({ userId: '', notes: '' });

  useEffect(() => {
    const loadEvents = async () => {
      try {
        console.log('Loading events for attendance...');
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        console.log('Today (start):', today.toISOString());

        // Query events that require attendance
        const eventsQuery = query(
          collection(db, 'events'),
          where('attendanceRequired', '==', true)
        );

        console.log('Executing events query...');
        const querySnapshot = await getDocs(eventsQuery);
        console.log(`Found ${querySnapshot.size} events`);

        const eventData: AttendanceEvent[] = [];
        querySnapshot.forEach((doc) => {
          const event = { id: doc.id, ...doc.data() } as AttendanceEvent;
          // Only include events from today onwards
          const eventDate = new Date(event.start || event.date);
          if (eventDate >= today) {
            console.log('Event:', {
              id: event.id,
              title: event.title,
              date: event.date,
              start: event.start,
              attendanceRequired: event.attendanceRequired
            });
            eventData.push(event);
          } else {
            console.log('Skipping past event:', {
              id: event.id,
              title: event.title,
              date: event.date,
              start: event.start
            });
          }
        });

        setEvents(eventData);
        console.log('Events loaded:', eventData.length);
        setLoading(false);
      } catch (error) {
        console.error('Error loading events:', error);
        toast.error('Failed to load events');
        setLoading(false);
      }
    };

    loadEvents();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      loadAttendanceRecords(selectedEvent.id);
    }
  }, [selectedEvent]);

  const loadAttendanceRecords = async (eventId: string) => {
    try {
      console.log('Loading attendance records for event:', eventId);
      const querySnapshot = await getDocs(
        query(
          collection(db, 'attendance'),
          where('eventId', '==', eventId)
        )
      );

      const records: AttendanceRecord[] = [];
      querySnapshot.forEach((doc) => {
        records.push({ id: doc.id, ...doc.data() } as AttendanceRecord);
      });

      console.log(`Found ${records.length} attendance records`);
      setAttendanceRecords(records);
    } catch (error) {
      console.error('Error loading attendance records:', error);
      toast.error('Failed to load attendance records');
    }
  };

  const handleManualAttendance = async () => {
    if (!selectedEvent || !manualAttendance.userId) return;

    try {
      await addDoc(collection(db, 'attendance'), {
        eventId: selectedEvent.id,
        userId: manualAttendance.userId,
        timestamp: new Date().toISOString(),
        method: 'manual',
        notes: manualAttendance.notes,
      });

      toast.success('Attendance recorded successfully');
      setManualAttendance({ userId: '', notes: '' });
      loadAttendanceRecords(selectedEvent.id);
    } catch (error) {
      console.error('Error recording attendance:', error);
      toast.error('Failed to record attendance');
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedEvent) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const csvData = event.target?.result as string;
        const rows = csvData.split('\n').slice(1); // Skip header row

        for (const row of rows) {
          const [userId, timestamp, notes] = row.split(',');
          if (userId) {
            await addDoc(collection(db, 'attendance'), {
              eventId: selectedEvent.id,
              userId: userId.trim(),
              timestamp: timestamp?.trim() || new Date().toISOString(),
              method: 'import',
              notes: notes?.trim(),
            });
          }
        }

        toast.success('Attendance records imported successfully');
        loadAttendanceRecords(selectedEvent.id);
      } catch (error) {
        console.error('Error importing attendance records:', error);
        toast.error('Failed to import attendance records');
      }
    };

    reader.readAsText(file);
  };

  const exportAttendance = () => {
    if (!selectedEvent) return;

    const csvContent = [
      ['User ID', 'Timestamp', 'Method', 'Notes'].join(','),
      ...attendanceRecords.map(record => 
        [
          record.userId,
          record.timestamp,
          record.method,
          record.notes || ''
        ].join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance-${selectedEvent.title}-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-text-secondary">Loading attendance...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-semibold text-text-primary">Attendance Tracking</h1>
            {selectedEvent && (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowQR(!showQR)}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                           transition-colors flex items-center gap-2"
                >
                  <QrCode className="w-5 h-5" />
                  {showQR ? 'Hide QR Code' : 'Show QR Code'}
                </button>
                <button
                  onClick={exportAttendance}
                  className="px-4 py-2 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 
                           transition-colors flex items-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Export
                </button>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Event Selection */}
            <div className="lg:col-span-1">
              <div className="bg-surface-light rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-text-primary mb-4">Events</h2>
                <div className="space-y-2">
                  {events.map((event) => (
                    <button
                      key={event.id}
                      onClick={() => setSelectedEvent(event)}
                      className={`w-full p-4 rounded-lg text-left transition-colors ${
                        selectedEvent?.id === event.id
                          ? 'bg-primary-50 border-2 border-primary-500'
                          : 'bg-background hover:bg-primary-50'
                      }`}
                    >
                      <h3 className="font-medium text-text-primary">{event.title}</h3>
                      <p className="text-sm text-text-secondary">
                        {format(new Date(event.start || event.date), 'PPP')}
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs bg-secondary-100 text-secondary-700 px-2 py-1 rounded-full">
                          {event.type}
                        </span>
                        {event.aiSummary && (
                          <span className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded-full">
                            {event.aiSummary.totalAttendees} attendees
                          </span>
                        )}
                      </div>
                    </button>
                  ))}

                  {events.length === 0 && (
                    <p className="text-text-secondary text-center py-4">
                      No events requiring attendance
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Attendance Management */}
            <div className="lg:col-span-2">
              {selectedEvent ? (
                <div className="space-y-6">
                  {/* QR Code */}
                  {showQR && (
                    <div className="bg-surface-light rounded-xl shadow-sm p-6 text-center">
                      <QRCode
                        value={`${window.location.origin}/check-in/${selectedEvent.id}`}
                        size={200}
                        className="mx-auto mb-4"
                      />
                      <p className="text-text-secondary">
                        Scan this QR code to mark attendance
                      </p>
                    </div>
                  )}

                  {/* Manual Attendance */}
                  <div className="bg-surface-light rounded-xl shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-text-primary mb-4">Manual Check-in</h2>
                    <div className="flex gap-4">
                      <input
                        type="text"
                        value={manualAttendance.userId}
                        onChange={(e) => setManualAttendance({
                          ...manualAttendance,
                          userId: e.target.value
                        })}
                        placeholder="User ID"
                        className="flex-1 px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                                 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                      />
                      <input
                        type="text"
                        value={manualAttendance.notes}
                        onChange={(e) => setManualAttendance({
                          ...manualAttendance,
                          notes: e.target.value
                        })}
                        placeholder="Notes (optional)"
                        className="flex-1 px-4 py-2 rounded-lg border border-border-light focus:border-primary-500 
                                 focus:ring-2 focus:ring-primary-500/20 outline-none transition-all"
                      />
                      <button
                        onClick={handleManualAttendance}
                        disabled={!manualAttendance.userId}
                        className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 
                                 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Check In
                      </button>
                    </div>
                  </div>

                  {/* Bulk Import */}
                  <div className="bg-surface-light rounded-xl shadow-sm p-6">
                    <h2 className="text-lg font-semibold text-text-primary mb-4">Bulk Import</h2>
                    <div className="flex items-center gap-4">
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="csv-upload"
                      />
                      <label
                        htmlFor="csv-upload"
                        className="px-4 py-2 bg-secondary-500 text-white rounded-lg hover:bg-secondary-600 
                                 transition-colors cursor-pointer flex items-center gap-2"
                      >
                        <Upload className="w-5 h-5" />
                        Upload CSV
                      </label>
                      <p className="text-sm text-text-secondary">
                        CSV format: User ID, Timestamp (optional), Notes (optional)
                      </p>
                    </div>
                  </div>

                  {/* Attendance Records */}
                  <div className="bg-surface-light rounded-xl shadow-sm p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-text-primary">
                        Attendance Records
                      </h2>
                      <div className="flex items-center gap-2">
                        <Users className="w-5 h-5 text-text-secondary" />
                        <span className="text-text-secondary">
                          {attendanceRecords.length} checked in
                        </span>
                      </div>
                    </div>

                    <div className="space-y-4">
                      {attendanceRecords.map((record) => (
                        <div
                          key={record.id}
                          className="flex items-center justify-between p-4 bg-background rounded-lg"
                        >
                          <div className="flex items-center gap-4">
                            <div className="p-2 bg-primary-50 rounded-lg">
                              <UserCheck className="w-5 h-5 text-primary-500" />
                            </div>
                            <div>
                              <p className="font-medium text-text-primary">{record.userId}</p>
                              <p className="text-sm text-text-secondary">
                                {format(new Date(record.timestamp), 'PPp')}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs bg-secondary-100 text-secondary-700 px-2 py-1 rounded-full">
                              {record.method}
                            </span>
                            {record.notes && (
                              <span className="text-sm text-text-secondary">{record.notes}</span>
                            )}
                          </div>
                        </div>
                      ))}

                      {attendanceRecords.length === 0 && (
                        <p className="text-text-secondary text-center py-4">
                          No attendance records yet
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-text-secondary">Select an event to manage attendance</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}